import { HDate } from '@hebcal/core';
import { DAYS_IN_HEBREW, MONTHS_IN_HEBREW } from '@/utils/const';
import { formatHebrewYear } from '@/utils/hebrew_year';
import { toHebrewPickerDateInputValue, toWallClockDate } from '@/utils/wall_clock_date';

/**
 * A Hebrew month grid, in the shape a month calendar draws.
 *
 * Every cell carries the Gregorian ISO day it stands for: the schedule stores,
 * compares and sends YYYY-MM-DD wall-clock days whichever calendar is on
 * screen, so a Hebrew grid changes only which cells are laid out and what is
 * printed on them.
 */

/** hebcal's month numbers: Nisan is 1 … Sh'vat 11, then Adar. */
const NISAN = 1;
const ELUL = 6;
const TISHREI = 7;

export interface HebrewCursor {
  year: number;
  /** hebcal month number, 1..13. */
  month: number;
}

export interface MonthGridDay {
  /** Day of the month, 1-based. */
  number: number;
  /** The Gregorian wall-clock day this cell stands for, YYYY-MM-DD. */
  iso: string;
  /** What the cell prints. */
  label: string;
}

// Sunday-first, the order the Hebrew week is read in — deliberately NOT the
// schedule engine's Monday-first week (SCHEDULE_DAY_KEYS). Keyed by the
// Gregorian weekday so a header :key stays stable across a calendar flip.
export const HEBREW_WEEKDAYS = [
  { key: 'sun', short: 'א׳', full: 'ראשון' },
  { key: 'mon', short: 'ב׳', full: 'שני' },
  { key: 'tue', short: 'ג׳', full: 'שלישי' },
  { key: 'wed', short: 'ד׳', full: 'רביעי' },
  { key: 'thu', short: 'ה׳', full: 'חמישי' },
  { key: 'fri', short: 'ו׳', full: 'שישי' },
  { key: 'sat', short: 'ש׳', full: 'שב״ק' },
];

/** 12, or 13 in a leap year — the last month number that year has. */
export const hebrewMonthsInYear = (year: number): number => HDate.monthsInYear(year);

// Adar II exists only in a leap year, and hebcal normalizes an impossible
// month silently instead of throwing — month 13 of a plain year becomes Adar,
// month 14 rolls into the NEXT year — so a cursor is clamped before it is ever
// handed to HDate.
export const clampHebrewCursor = (cursor: HebrewCursor): HebrewCursor => {
  const rawYear = Math.trunc(Number(cursor?.year));
  const year = Number.isFinite(rawYear) && rawYear > 0 ? rawYear : new HDate().getFullYear();
  const last = hebrewMonthsInYear(year);
  const rawMonth = Math.trunc(Number(cursor?.month));
  const month = Math.min(Math.max(NISAN, Number.isFinite(rawMonth) ? rawMonth : NISAN), last);
  return { year, month };
};

// Indexed by hebcal's month NUMBER, never by getMonthName(): hebcal@2 spells
// the leap months "Adar 1"/"Adar 2" while HEBREW_MONTHS holds "Adar I"/"Adar
// II", so a name lookup silently misses in every leap year. Numbers cannot
// drift between the two packages this app has installed.
export const hebrewMonthName = (month: number, year: number): string => {
  if (month >= 1 && month <= 11) return MONTHS_IN_HEBREW[month - 1];
  if (month === 13) return MONTHS_IN_HEBREW[13]; // אדר ב
  return hebrewMonthsInYear(year) === 13
    ? MONTHS_IN_HEBREW[12] // אדר א
    : MONTHS_IN_HEBREW[11]; // אדר
};

/** e.g. 'תשרי תשפ"ז' — a calendar header. */
export const hebrewMonthLabel = (cursor: HebrewCursor): string => {
  const { month, year } = clampHebrewCursor(cursor);
  return `${hebrewMonthName(month, year)} ${formatHebrewYear(year)}`;
};

/** e.g. 'י"ד ניסן' — a day and its month, for a calendar cell. '' for anything unusable. */
export const formatHebrewDayMonthLabel = (value: unknown): string => {
  const date = toWallClockDate(value);
  if (!date) return '';
  const hebrew = new HDate(date);
  const day = DAYS_IN_HEBREW[hebrew.getDate() - 1] || String(hebrew.getDate());
  return `${day} ${hebrewMonthName(hebrew.getMonth(), hebrew.getFullYear())}`;
};

/** e.g. 'י"ד ניסן תשפ"ו' — a single date label. '' for anything unusable. */
export const formatHebrewDateLabel = (value: unknown): string => {
  const date = toWallClockDate(value);
  if (!date) return '';
  return `${formatHebrewDayMonthLabel(value)} ${formatHebrewYear(new HDate(date).getFullYear())}`;
};

/** The Hebrew month a Gregorian day falls in. */
export const hebrewCursorFor = (value: unknown): HebrewCursor | null => {
  const date = toWallClockDate(value);
  if (!date) return null;
  const hebrew = new HDate(date);
  return { year: hebrew.getFullYear(), month: hebrew.getMonth() };
};

/**
 * The Gregorian ISO day a given day of a Hebrew month falls on. `day` is
 * clamped to the month, so 15 is always a real day (a Hebrew month is 29 or
 * 30 days).
 */
export const hebrewMonthDayIso = (cursor: HebrewCursor, day = 1): string => {
  const { month, year } = clampHebrewCursor(cursor);
  const count = new HDate(1, month, year).daysInMonth();
  const safe = Math.min(Math.max(1, Math.trunc(Number(day)) || 1), count);
  return toHebrewPickerDateInputValue(new HDate(safe, month, year));
};

// The year number turns over at Tishrei while the month numbers start at
// Nisan, so chronological order inside year Y is 7,8,9,10,11,12,(13),1..6.
// The two wraps are therefore asymmetric: Elul -> Tishrei moves the year,
// Adar -> Nisan does not. Modular arithmetic cannot say that; keep the
// branches. (Same shape as Jewish_Date_Picker.changeMonth.)
const stepOnce = (cursor: HebrewCursor, direction: number): HebrewCursor => {
  let { month, year } = cursor;
  if (direction > 0) {
    if (month === ELUL) {
      month = TISHREI;
      year += 1;
    } else if (month === hebrewMonthsInYear(year)) {
      month = NISAN;
    } else {
      month += 1;
    }
  } else if (month === TISHREI) {
    month = ELUL;
    year -= 1;
  } else if (month === NISAN) {
    month = hebrewMonthsInYear(year);
  } else {
    month -= 1;
  }
  return clampHebrewCursor({ year, month });
};

export const stepHebrewMonth = (cursor: HebrewCursor, delta: number): HebrewCursor => {
  const direction = delta < 0 ? -1 : 1;
  let next = clampHebrewCursor(cursor);
  for (let step = 0; step < Math.abs(delta); step += 1) next = stepOnce(next, direction);
  return next;
};

/** Null cells are the leading and trailing blanks that pad out whole weeks. */
export const buildHebrewMonthGrid = (cursor: HebrewCursor): (MonthGridDay | null)[] => {
  const { month, year } = clampHebrewCursor(cursor);
  const first = new HDate(1, month, year);
  // getDay() delegates to greg().getDay(), so 0 = Sunday — which IS the
  // leading-blank count for a Sunday-first week. No (getDay() + 6) % 7 shift:
  // that would be a Monday-first week.
  const leading = first.getDay();
  const count = first.daysInMonth(); // 29 or 30, never 31
  const total = Math.ceil((leading + count) / 7) * 7;

  return Array.from({ length: total }, (_, index) => {
    const number = index - leading + 1;
    if (number < 1 || number > count) return null;
    return {
      number,
      // greg() is a Date at local midnight and this reads its local parts.
      // Never toISOString(), which rolls the day west of UTC.
      iso: toHebrewPickerDateInputValue(new HDate(number, month, year)),
      label: DAYS_IN_HEBREW[number - 1] || String(number),
    };
  });
};
